import { inject, Service } from '@angular/core';
import type {
  I18nLanguage,
  I18nLanguageRecord,
  OnyxDropdownOptionsSourceResult,
  OnyxOption,
} from '@onyx/angular';
import { I18N_LANGUAGES, OnyxLanguagePipe } from '@onyx/angular';
import type { Observable } from 'rxjs';
import { map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { ApiService } from '../../../../../common/services/api.service';
import { LanguageService } from '../../../../../common/services/language.service';
import type {
  CompanyDictionaryByCode,
  CompanyDictionaryCode,
  CompanyDictionaryFormByCode,
} from '../enums/company-dictionary-code';

export interface CompanyDictionariesFilters {
  query?: string;
}

@Service()
export class CompanyDictionariesService extends ApiService {
  private readonly languageService = inject(LanguageService);
  private readonly languagePipe = inject(OnyxLanguagePipe);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listDictionary<T extends CompanyDictionaryCode>(
    type: T,
    params?: CompanyDictionariesFilters,
  ): Observable<CompanyDictionaryByCode[T][]> {
    return this.get(`/business/dictionaries/${type}`, {
      params: { ...(params?.query && { query: params.query }) },
    });
  }

  public searchDictionary<T extends CompanyDictionaryCode>(
    type: T,
    query: string,
  ): Observable<OnyxDropdownOptionsSourceResult<CompanyDictionaryByCode[T]>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listDictionary(type, { query })),
      map((items) => ({
        options: items.map((item): OnyxOption<CompanyDictionaryByCode[T]> => ({
          name: this.languagePipe.transform(item.names),
          value: item,
        })),
        totalItems: items.length,
      })),
    );
  }

  public getDictionary<T extends CompanyDictionaryCode>(
    uuid: string,
  ): Observable<CompanyDictionaryByCode[T]> {
    return this.get(`/business/dictionaries/${uuid}`);
  }

  public addDictionaryItem<T extends CompanyDictionaryCode>(
    type: T,
    form: CompanyDictionaryFormByCode[T],
  ): Observable<void> {
    return of(this.fillEmptyNames(form)).pipe(
      switchMap((form) =>
        this.post<void>(`/business/dictionaries/${type}`, form),
      ),
      tap(() => this._reload$.next()),
    );
  }

  public editDictionaryItem<T extends CompanyDictionaryCode>(
    uuid: string,
    form: CompanyDictionaryFormByCode[T],
  ): Observable<void> {
    return of(this.fillEmptyNames(form)).pipe(
      switchMap((form) =>
        this.put<void>(`/business/dictionaries/${uuid}`, form),
      ),
      tap(() => this._reload$.next()),
    );
  }

  public deleteDictionaryItem(uuid: string): Observable<void> {
    return this.delete<void>(`/business/dictionaries/${uuid}`).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public suggestTranslations(
    language: I18nLanguage,
    name: string,
  ): Observable<I18nLanguageRecord<string>> {
    return this.post('/business/dictionary/translation', { language, name });
  }

  private fillEmptyNames<T extends CompanyDictionaryCode>(
    form: CompanyDictionaryFormByCode[T],
  ): CompanyDictionaryFormByCode[T] {
    const defaultLanguage = this.languageService.getLanguage();
    const defaultTranslation = form.names[defaultLanguage];

    return {
      ...form,
      names: Object.fromEntries(
        I18N_LANGUAGES.map((language) => [
          language,
          form.names[language] || defaultTranslation,
        ]),
      ),
    };
  }
}
